import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { InvoiceStatus } from "enums/InvoiceStatus";
import InvoicePreview from "components/admin/invoices/InvoicePreview";
import LoadingWithoutModal from "components/common/LoadingWithoutModal";
import { Box, Button, Card, CardContent, Typography } from "@mui/material";
import { Stack } from "components/layout/reusableLayouts";
import contactHttpClient from "utils/contactHttpClient";
import useGetInvoiceStatusDetails from "hooks/useGetInvoiceStatusDetails";
import { useReactToPrint } from "react-to-print";
import { useLogoForScheme } from "utils/themeContextHelper";
import AcquirerCheckout from "components/checkout/AcquirerCheckout";
import PayLinkPaymentMethodPicker from "components/payLink/PayLinkPaymentMethodPicker";
import {
  CheckoutBillingDefaults,
  CheckoutGuardResult,
  CheckoutSessionPayload,
} from "components/checkout/types";
import ContactType from "enums/ContactType";
import { BREAKPOINTS } from "theme/outpaveTheme";
import { useStripePaymentIntent } from "hooks/useStripePaymentIntent";
import { useGetInvoicePaymentDetails } from "hooks/useGetInvoicePaymentDetails";
import PaymentDetailsBox from "components/admin/invoices/recievePayment/PaymentDetailsBox";
import AutoPayManager from "components/admin/invoices/recievePayment/AutoPayManager";
import { useContactAuth } from "hooks/useContactAuthContext";
import { getInvoicePayToken } from "utils/invoices/invoicePayToken";
import ContactLoginDialog from "components/auth/ContactLoginDialog";
import InvoiceHeaderBox from "components/admin/invoices/recievePayment/InvoiceHeaderBox";
import EstimateActionBox from "components/admin/invoices/recievePayment/EstimateActionsBox";

const BASE_URL = process.env.REACT_APP_URL;

const PayInvoice: FC = () => {
  const { t } = useTranslation();
  const { contactToken, isContactAuthenticated, logoutContact } =
    useContactAuth();
  const [signInOpen, setSignInOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [hasAttemptedLoadingInvoice, setHasAttemptedLoadingInvoice] =
    useState<boolean>(false);
  const [invoice, setInvoice] = useState<any>(null);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [showInvoice, setShowInvoice] = useState(
    () => window.innerWidth >= BREAKPOINTS.xl,
  );
  const [isWideLayout, setIsWideLayout] = useState(
    () => window.innerWidth >= BREAKPOINTS.xl,
  );

  useEffect(() => {
    const handleResize = () =>
      setIsWideLayout(window.innerWidth >= BREAKPOINTS.xl);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [loadingInvoice, setLoadingInvoice] = useState<boolean>(false);
  const [approveOrRejectLoading, setApproveOrRejectLoading] =
    useState<boolean>(false);
  const [isPrintLoading, setIsPrintLoading] = useState<boolean>(false);

  const printRef = useRef<HTMLDivElement>(null);

  const invoiceIdFromUrl = new URLSearchParams(location.search).get("id");
  const recipientIdFromUrl = new URLSearchParams(location.search).get(
    "recipientId",
  );
  const manualStartCheckout =
    new URLSearchParams(location.search).get("startCheckout") === "true";

  const {
    clientSecret,
    customerSessionClientSecret,
    acquirer,
    publishableKey,
    componentType,
    allowedPaymentMethods,
    paymentMethodType,
    changePaymentMethod,
    loading: loadingPaymentIntent,
    startCheckout,
  } = useStripePaymentIntent(contactToken);

  const {
    isPaid,
    isCanceled,
    isInvoiceSent,
    isPaymentProcessing,
    isFailedPayment,
    isEstimate,
    isSentOrFailed,
  } = useGetInvoiceStatusDetails(invoice?.status as InvoiceStatus);

  const shouldFetchPaymentDetails =
    isPaid || isPaymentProcessing || isFailedPayment;

  const {
    paymentDetails,
    loading: loadingPaymentDetails,
    error: paymentDetailsError,
    fetchInvoicePaymentDetails: refetchPaymentDetails,
  } = useGetInvoicePaymentDetails(
    shouldFetchPaymentDetails ? invoice.id : null,
    recipientIdFromUrl,
  );

  const invoiceExists = invoice?.id;
  const attachmentExists = attachments.length > 0;
  const isCheckoutRoute = location.pathname.includes("/pay-invoice/checkout");
  // A first load has the cards' own skeletons; the overlay is for work started on a page already drawn.
  const isLoading =
    approveOrRejectLoading ||
    (loadingInvoice && !!invoiceExists) ||
    (loadingPaymentIntent && !!clientSecret) ||
    (loadingPaymentDetails && !!paymentDetails);

  // Only auto-start checkout for a first-time (SENT) invoice; a FAILED one should land on the
  // "Payment Failed" screen with the retry button instead of silently jumping into a new payment
  const canStartCheckout =
    invoiceExists &&
    (manualStartCheckout || isInvoiceSent) &&
    isCheckoutRoute &&
    recipientIdFromUrl;

  const canShowCheckout =
    invoiceExists && isCheckoutRoute && clientSecret && isSentOrFailed;

  const showEstimateActionBox = invoiceExists && isEstimate;

  const checkoutSession: CheckoutSessionPayload | null =
    acquirer && clientSecret
      ? {
          acquirer,
          clientSecret,
          customerSessionClientSecret: customerSessionClientSecret ?? undefined,
          publishableKey: publishableKey ?? "",
          componentType: componentType ?? "",
        }
      : null;

  // Preserve the pay token across the acquirer's redirect for guest checkout.
  const payToken = getInvoicePayToken();
  const checkoutReturnUrl = `${BASE_URL}/pay-invoice/complete?id=${invoiceIdFromUrl}&recipientId=${recipientIdFromUrl}${
    payToken ? `&t=${payToken}` : ""
  }`;

  const recipient = invoice?.primaryRecipient;
  const billingDefaults: CheckoutBillingDefaults = {
    firstName:
      recipient?.contactType === ContactType.Business
        ? recipient?.businessName || ""
        : recipient?.firstName || "",
    lastName:
      recipient?.contactType === ContactType.Business
        ? ""
        : recipient?.lastName || "",
    phone: recipient?.phone || "",
    address: recipient?.address,
  };

  /* Lock the invoice before confirmation to prevent two tabs from paying it. */
  const lockInvoiceForPayment = async (): Promise<CheckoutGuardResult> => {
    try {
      const { data } = await contactHttpClient.post(
        `/payments/invoice/${invoiceIdFromUrl}/${recipientIdFromUrl}/lock-for-payment`,
      );
      return { ok: !!data.success, message: data.error };
    } catch (error) {
      return { ok: false, message: responseErrorOf(error) };
    }
  };

  const unlockInvoiceForPayment = async () => {
    try {
      await contactHttpClient.post(
        `/payments/invoice/${invoiceIdFromUrl}/${recipientIdFromUrl}/unlock-for-payment`,
      );
    } catch (error) {
      console.error("Error unlocking the invoice:", error);
    }
  };

  useEffect(() => {
    if (invoiceIdFromUrl && recipientIdFromUrl) {
      fetchInvoice(invoiceIdFromUrl, recipientIdFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceIdFromUrl, recipientIdFromUrl]);

  useEffect(() => {
    if (canStartCheckout) {
      startCheckout({
        invoiceId: invoice.id,
        recipientId: recipientIdFromUrl,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canStartCheckout, contactToken]);

  const fetchInvoice = async (invoiceId: string, recipientId: string) => {
    setLoadingInvoice(true);
    setHasAttemptedLoadingInvoice(true);
    try {
      const response = await contactHttpClient.get(
        `/invoice/${invoiceId}/${recipientId}`,
        { suppressAuthToast: true },
      );
      setInvoice(response.data);

      fetchInvoiceAttachments(invoiceId, recipientId);
    } catch (error: any) {
      if ([401, 403].includes(error.response?.status)) {
        // Older links have no pay token; sign in and return to the same invoice.
        // Clear an expired or unauthorized session so login does not bounce back.
        logoutContact();
        navigate("/contact/login", {
          replace: true,
          state: { from: location },
        });
        return;
      }
      console.error("Error fetching invoice:", error);
    } finally {
      setLoadingInvoice(false);
    }
  };

  // Only while awaiting microdeposit verification: refetch when the payer returns to this tab (e.g. after
  // verifying in Stripe's tab) so the screen advances once the PI leaves requires_action
  useEffect(() => {
    if (!paymentDetails?.microdepositVerificationUrl) return;
    const onFocus = () => {
      if (invoiceIdFromUrl && recipientIdFromUrl) {
        fetchInvoice(invoiceIdFromUrl, recipientIdFromUrl);
        refetchPaymentDetails();
      }
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    paymentDetails?.microdepositVerificationUrl,
    invoiceIdFromUrl,
    recipientIdFromUrl,
  ]);

  const fetchInvoiceAttachments = async (
    invoiceId: string,
    recipientId: string,
  ) => {
    try {
      const response = await contactHttpClient.get(
        `/api/v1/attachments/invoice/${invoiceId}/${recipientId}`,
      );
      const attachmentsData = response.data.attachmentsAccumulated;
      const attachments = attachmentsData.map((attachment: any) => ({
        id: attachment.id,
        subParentId: attachment.subParentId,
        name: attachment.name,
        description: attachment.description,
        type: attachment.fileType,
        assetUrl: attachment.file.assetUrl,
        extension: attachment.file.extension,
      }));
      setAttachments(attachments);
    } catch (error) {
      console.error("Error fetching attachments:", error);
    }
  };

  const handleAfterPrint = useCallback(() => {
    setIsPrintLoading(false);
  }, []);

  const handleBeforePrint = useCallback(() => {
    setIsPrintLoading(true);
    return Promise.resolve();
  }, []);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: "Invoice",
    onBeforePrint: handleBeforePrint,
    onAfterPrint: handleAfterPrint,
  });

  let statusForBox: string = "loadingInvoiceOrPayment";

  if (!invoiceIdFromUrl || !recipientIdFromUrl) {
    statusForBox = "invalidUrl";
  } else if (isCanceled) {
    statusForBox = "canceled";
  } else if (
    shouldFetchPaymentDetails &&
    !paymentDetails &&
    !paymentDetailsError
  ) {
    // The outcome card waits for its payment details rather than drawing without them.
    statusForBox = "loadingInvoiceOrPayment";
  } else if (paymentDetails?.microdepositVerificationUrl) {
    // Bank pending microdeposit verification: show the verify screen instead of "processing"
    statusForBox = "verify_bank";
  } else if (isPaymentProcessing) {
    statusForBox = "processing";
  } else if (paymentDetails?.paymentIntentStatus) {
    statusForBox = paymentDetails.paymentIntentStatus;
  } else if (isPaid) {
    statusForBox = "succeeded";
  } else if (
    loadingInvoice ||
    loadingPaymentDetails ||
    loadingPaymentIntent ||
    !hasAttemptedLoadingInvoice
  ) {
    statusForBox = "loadingInvoiceOrPayment";
  } else if (!invoiceExists) {
    statusForBox = "couldNotLoad";
  } else {
    statusForBox = "otherStatus";
  }

  return (
    <Box
      sx={{
        backgroundColor: "var(--color-bg-page)",
        minHeight: "100vh",
        position: "relative",
      }}
    >
      <Logo />

      <Box sx={{ display: "flex", flexDirection: "row", minHeight: "100vh" }}>
        {/* LEFT COLUMN */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            paddingTop: "var(--space-15)",
            paddingBottom: "var(--space-9)",
            paddingInline: "var(--space-5)",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              padding: "var(--space-8)",
            }}
          >
            {isContactAuthenticated && (
              <Button
                variant="primary-outlined"
                sx={{ width: "auto" }}
                onClick={() => logoutContact()}
              >
                {t("Logout")}
              </Button>
            )}
          </Box>

          {/* Main payment content, centered */}
          <Stack gap="var(--space-5)" sx={{ width: { xs: "100%", md: 500 } }}>
            <InvoiceHeaderBox
              invoice={invoice}
              isInvoiceLoading={loadingInvoice}
              showInvoice={showInvoice}
              onToggleInvoice={() => setShowInvoice((prev) => !prev)}
              onPrint={handlePrint}
              isPrintLoading={isPrintLoading}
            />
            {showEstimateActionBox ? (
              isContactAuthenticated ? (
                <EstimateActionBox
                  invoiceStatus={invoice?.status}
                  fetchInvoice={fetchInvoice}
                  invoiceId={invoiceIdFromUrl!}
                  recipientId={recipientIdFromUrl!}
                  setApproveOrRejectLoading={setApproveOrRejectLoading}
                />
              ) : (
                <SignInPrompt
                  message={t("payInvoice.SignInToDecideEstimate")}
                  label={t("Sign in")}
                  onSignIn={() => setSignInOpen(true)}
                />
              )
            ) : canShowCheckout && checkoutSession ? (
              <Card>
                <CardContent>
                  <PayLinkPaymentMethodPicker
                    methods={allowedPaymentMethods}
                    quotes={null}
                    currency={invoice?.currency || "usd"}
                    selected={paymentMethodType}
                    chosen
                    disabled={loadingPaymentIntent}
                    onSelect={(method) =>
                      changePaymentMethod({
                        paymentMethodType: method,
                        invoiceId: invoiceIdFromUrl!,
                        recipientId: recipientIdFromUrl!,
                      })
                    }
                    renderExpanded={() =>
                      loadingPaymentIntent ? null : (
                        <AcquirerCheckout
                          key={paymentMethodType}
                          session={checkoutSession}
                          returnUrl={checkoutReturnUrl}
                          billingDefaults={billingDefaults}
                          onBeforeConfirm={lockInvoiceForPayment}
                          onConfirmFailed={unlockInvoiceForPayment}
                        />
                      )
                    }
                  />
                </CardContent>
              </Card>
            ) : (
              <PaymentDetailsBox
                paymentIntentStatus={statusForBox}
                paymentMethodDetails={paymentDetails?.paymentMethodDetails}
                invoiceData={invoice}
                error={paymentDetails?.error}
                verificationUrl={paymentDetails?.microdepositVerificationUrl}
                onTryAgain={() => {
                  if (
                    !isCheckoutRoute &&
                    invoiceIdFromUrl &&
                    recipientIdFromUrl
                  ) {
                    navigate(
                      `/pay-invoice/checkout?id=${invoiceIdFromUrl}&recipientId=${recipientIdFromUrl}&startCheckout=true`,
                    );
                  } else if (invoiceIdFromUrl && recipientIdFromUrl) {
                    startCheckout({
                      invoiceId: invoiceIdFromUrl,
                      recipientId: recipientIdFromUrl,
                    });
                  }
                }}
              />
            )}

            {invoiceExists &&
              recipientIdFromUrl &&
              (isContactAuthenticated ? (
                <AutoPayManager
                  recipientId={recipientIdFromUrl}
                  invoiceId={invoiceIdFromUrl}
                />
              ) : (
                <SignInPrompt
                  message={
                    isPaid
                      ? t("payInvoice.SignInToManageAutoPay")
                      : t("payInvoice.SignInForSavedMethods")
                  }
                  label={
                    isPaid
                      ? t("payInvoice.ManageAutomaticPayments")
                      : t("Use a saved payment method")
                  }
                  onSignIn={() => setSignInOpen(true)}
                />
              ))}
          </Stack>
        </Box>

        {/* RIGHT COLUMN: Invoice Preview (visible version) */}
        {(isInvoiceSent || isPaid || isFailedPayment || isEstimate) &&
          invoiceExists &&
          showInvoice && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                backgroundColor: "var(--color-bg-surface)",
                overflow: "auto",
                ...(isWideLayout
                  ? { minHeight: "100vh", width: "210mm" }
                  : {
                      position: "fixed",
                      inset: 0,
                      zIndex: 50,
                      width: "100%",
                      height: "100%",
                    }),
              }}
            >
              <Box
                sx={{
                  display: { xs: "flex", xl: "none" },
                  justifyContent: "flex-start",
                  padding: "var(--space-8)",
                }}
              >
                <Button
                  variant="primary-outlined"
                  sx={{ width: "auto" }}
                  onClick={() => setShowInvoice(false)}
                >
                  {t("Hide Invoice")}
                </Button>
              </Box>
              <Box>
                <InvoicePreview
                  invoice={invoice}
                  showShadow={false}
                  attachments={attachmentExists ? attachments : null}
                />
              </Box>
            </Box>
          )}

        {/* Always-rendered, visually hidden Invoice Preview for printing since the print ref must always be rendered for print to work */}
        {invoiceExists && isPrintLoading && (
          <Box
            ref={printRef}
            sx={{
              display: "flex",
              flexDirection: "column",
              backgroundColor: "var(--color-bg-surface)",
              minHeight: "100vh",
              width: "210mm",
              overflow: "auto",
              zIndex: -1,
              position: "absolute",
              top: 0,
              left: 0,
            }}
          >
            <InvoicePreview
              invoice={invoice}
              showShadow={false}
              attachments={attachmentExists ? attachments : null}
            />
          </Box>
        )}
      </Box>

      <ContactLoginDialog
        open={signInOpen}
        email={invoice?.primaryRecipient?.email}
        onAuthenticated={() => setSignInOpen(false)}
        onClose={() => setSignInOpen(false)}
      />

      <LoadingWithoutModal open={isLoading} />
    </Box>
  );
};

const SignInPrompt: FC<{
  message: string;
  label: string;
  onSignIn: () => void;
}> = ({ message, label, onSignIn }) => (
  <Card variant="outlined">
    <CardContent>
      <Stack
        gap="var(--space-3)"
        sx={{ alignItems: "center", textAlign: "center" }}
      >
        <Typography
          variant="body2"
          sx={{ color: "var(--color-text-secondary)" }}
        >
          {message}
        </Typography>
        <Button variant="default-outlined" onClick={onSignIn}>
          {label}
        </Button>
      </Stack>
    </CardContent>
  </Card>
);

export default PayInvoice;

const Logo: FC = () => {
  const logo = useLogoForScheme();

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        padding: "var(--space-8)",
      }}
    >
      <Box
        component="img"
        src={logo}
        alt="Company Logo"
        sx={{ height: 40, width: "auto", maxWidth: 200 }}
        aria-label="Company Logo"
        draggable={false}
      />
    </Box>
  );
};

const responseErrorOf = (error: unknown): string | undefined =>
  (error as { response?: { data?: { error?: string } } })?.response?.data
    ?.error;
